import { Edge } from "@xyflow/react";

import { Operation } from "src/api/flowVersionUpdateIndex";
import { SplitBranch } from "src/clients/flow-api";
import {
  getDefaultFlowEdge,
  getDefaultSplitBranchNodeV2,
  getDefaultSplitEdge,
} from "src/constants/DefaultValues";
import {
  BeMappedNode,
  GroupNode,
  SplitNodeV2,
  SplitNodeV2Data,
} from "src/constants/NodeDataTypes";
import { GraphStore } from "src/store/GraphStore";
import {
  findBranchNodes,
  getParentGroup,
  getUpdatedGroup,
} from "src/utils/GraphUtils";

export type SplitBranchWithName = SplitBranch & { name: string };
export const resolveBranchName = (
  branch: SplitBranch,
  index: number,
  existingLabel: string | undefined,
): string => {
  // Use copilot-provided name if present
  const copilotName = (branch as SplitBranchWithName).name;
  if (copilotName) {
    return copilotName;
  }
  // Use existing branch node label if available
  if (existingLabel !== undefined) {
    return existingLabel;
  }
  // Fallback for new branches
  return `Branch ${index + 1}`;
};

type SyncResult = {
  nodesUpdate: Record<string, BeMappedNode | null>;
  edgesUpdate: Record<string, Edge | null>;
  groupsUpdate: Record<string, GroupNode | null>;
  operation: Operation;
  branchToDeleteInfo?: {
    branchName: string;
    branchCount: number;
    nodesToDelete: BeMappedNode[];
    branchNodeIds: string[];
  };
};

/**
 * Handles deletion of branches for split node suggestions.
 * Collects all nodes and edges that need to be deleted.
 */
const handleBranchDeletionsForSplitNodeSuggestion = (
  graphStore: GraphStore,
  branchesToDelete: SplitBranch[],
  splitNode: SplitNodeV2,
  currentEdges: Edge[],
): { deletedNodeIds: Set<string>; edgesToDelete: Set<Edge> } => {
  const deletedNodeIds = new Set<string>();
  const edgesToDelete = new Set<Edge>();

  for (const deletedBranch of branchesToDelete) {
    const splitToBranchEdge = graphStore.edges.get(deletedBranch.edge_id);
    if (!splitToBranchEdge) {
      continue;
    }

    const branchNode = graphStore.nodes.get(splitToBranchEdge.target);
    if (!branchNode) {
      continue;
    }

    const nodesInBranch = findBranchNodes(
      branchNode,
      graphStore.nodesArray,
      currentEdges,
      splitNode,
    );

    // add all nodes in the branch to the deleted node ids
    nodesInBranch.forEach((node) => {
      deletedNodeIds.add(node.id);
    });

    // Find all edges that need to be deleted for this branch
    // 1. The edge from split node to branch node (deletedBranch.edge_id)
    // 2. All edges where source is a node on the branch (outgoing edges)
    // 3. All edges where target is a node on the branch (incoming edges)
    const branchEdgesToDelete = currentEdges.filter(
      (edge: Edge) =>
        edge.id === deletedBranch.edge_id ||
        deletedNodeIds.has(edge.source) ||
        deletedNodeIds.has(edge.target),
    );

    branchEdgesToDelete.forEach((edge) => {
      edgesToDelete.add(edge);
    });
  }

  return { deletedNodeIds, edgesToDelete };
};

/**
 * Handles addition of branches for split node suggestions.
 * Creates new branch nodes and their associated edges.
 */
const handleBranchAdditionsForSplitNodeSuggestion = (
  branchesToAdd: SplitBranchWithName[],
  splitNode: SplitNodeV2,
  mergeNode: BeMappedNode,
): {
  nodesToCreate: Record<string, BeMappedNode>;
  edgesToCreate: Record<string, Edge>;
} => {
  const nodesToCreate: Record<string, BeMappedNode> = {};
  const edgesToCreate: Record<string, Edge> = {};

  branchesToAdd.forEach((branchToAdd) => {
    const newBranchNode = getDefaultSplitBranchNodeV2({
      name: branchToAdd.name,
    });

    const splitToBranchEdge = getDefaultSplitEdge({
      sourceId: splitNode.id,
      targetId: newBranchNode.id,
      id: branchToAdd.edge_id,
    });

    const branchToMergeEdge = getDefaultFlowEdge({
      sourceId: newBranchNode.id,
      targetId: mergeNode.id,
    });

    nodesToCreate[newBranchNode.id] = newBranchNode;
    edgesToCreate[splitToBranchEdge.id] = splitToBranchEdge;
    edgesToCreate[branchToMergeEdge.id] = branchToMergeEdge;
  });

  return { nodesToCreate, edgesToCreate };
};

/**
 * Calculates group updates when nodes are deleted or added for split node suggestions.
 * Updates groups to remove deleted nodes and add new branch nodes.
 * Deletes groups that have all their children removed.
 */
const getGroupUpdatesForSplitNodeSuggestion = (
  graphStore: GraphStore,
  deletedNodeIds: Set<string>,
  newBranchNodeIds: string[],
  splitNode: SplitNodeV2,
): Record<string, GroupNode | null> => {
  const deletedNodeIdsArray = Array.from(deletedNodeIds);
  const groupsToUpdate: Record<string, GroupNode> = {};
  const groupsToDelete: GroupNode[] = [];

  // Update groups for deleted nodes
  for (const deletedNodeId of deletedNodeIdsArray) {
    const parentGroup = getParentGroup(graphStore.groups, deletedNodeId);
    if (parentGroup && !groupsToUpdate[parentGroup.id]) {
      const updatedChildrenIds = parentGroup.data.childrenIDs.filter(
        (childId) => !deletedNodeIds.has(childId),
      );
      groupsToUpdate[parentGroup.id] = getUpdatedGroup({
        group: parentGroup,
        updatedChildrenIds,
      });
    }
  }

  // Update groups for added nodes
  if (newBranchNodeIds.length > 0) {
    const parentGroup = getParentGroup(graphStore.groups, splitNode.id);
    if (parentGroup) {
      const existingChildren = groupsToUpdate[parentGroup.id]
        ? groupsToUpdate[parentGroup.id].data.childrenIDs
        : parentGroup.data.childrenIDs;
      groupsToUpdate[parentGroup.id] = getUpdatedGroup({
        group: parentGroup,
        updatedChildrenIds: [
          ...existingChildren.filter((id) => !deletedNodeIds.has(id)),
          ...newBranchNodeIds,
        ],
      });
    }
  }

  // Find groups that should be deleted
  for (const [_, group] of graphStore.groups) {
    if (
      group.data.childrenIDs.length > 0 &&
      group.data.childrenIDs.every((id) => deletedNodeIds.has(id))
    ) {
      groupsToDelete.push(group);
    }
  }

  return {
    ...Object.fromEntries(
      Object.entries(groupsToUpdate).map(([id, group]) => [id, group]),
    ),
    ...Object.fromEntries(groupsToDelete.map((group) => [group.id, null])),
  };
};

/**
 * Collects branch deletion info for the confirmation modal.
 * Returns information about which branches and nodes will be deleted.
 */
const getBranchDeletionInfo = (
  graphStore: GraphStore,
  branchesToDelete: SplitBranch[],
  splitNode: SplitNodeV2,
  currentEdges: Edge[],
):
  | {
      branchName: string;
      branchCount: number;
      branchNodeIds: string[];
      nodesToDelete: BeMappedNode[];
    }
  | undefined => {
  if (branchesToDelete.length === 0) {
    return undefined;
  }

  const allNodesToDelete = new Set<BeMappedNode>();
  const branchNames: string[] = [];
  const branchNodeIds: string[] = [];

  for (const deletedBranch of branchesToDelete) {
    const splitToBranchEdge = graphStore.edges.get(deletedBranch.edge_id);
    if (splitToBranchEdge) {
      const branchNode = graphStore.nodes.get(splitToBranchEdge.target);
      if (branchNode) {
        branchNames.push(branchNode.data.label);
        branchNodeIds.push(branchNode.id);
        const nodesInBranch = findBranchNodes(
          branchNode,
          graphStore.nodesArray,
          currentEdges,
          splitNode,
        );
        nodesInBranch.forEach((node) => allNodesToDelete.add(node));
      }
    }
  }

  if (allNodesToDelete.size === 0) {
    return undefined;
  }

  // Filter out branch nodes themselves by ID (not by label, to avoid
  // filtering out nested nodes that happen to have the same name)
  const branchNodeIdSet = new Set(branchNodeIds);
  const nodesWithoutBranchNodes = Array.from(allNodesToDelete).filter(
    (node) => !branchNodeIdSet.has(node.id),
  );

  // Use first branch name if single branch, or combined message if multiple
  const branchName =
    branchNames.length === 1
      ? branchNames[0]
      : `${branchNames.length} branches`;

  return {
    branchName,
    branchCount: branchNames.length,
    branchNodeIds,
    nodesToDelete: nodesWithoutBranchNodes,
  };
};

/**
 * Syncs edges and nodes for a split node Copilot suggestion.
 *
 * Ensures the graph remains connected when Copilot modifies split node branches by handling:
 * - **Adding branches**: Creates branch nodes, edges (split→branch→merge), and updates groups
 * - **Deleting branches**: Removes branch nodes, downstream nodes, edges, and updates/deletes groups.
 *   Returns branchToDeleteInfo for confirmation modal.
 * - **Content changes**: Updates split node with new branch clauses (no structural changes)
 *
 * Returns undefined if no changes needed, otherwise SyncResult with updates.
 * Includes branchToDeleteInfo when branches need deletion (for confirmation modal).
 *
 * Without these updates, the graph would break because Copilot only updates nodes, not edges or groups.
 */
export const syncEdgesAndNodesForSplitNodeSuggestion = (
  graphStore: GraphStore,
  nodeId: string,
  suggestionMeta: SplitNodeV2Data,
): SyncResult | undefined => {
  const splitNode = graphStore.nodes.get(nodeId) as SplitNodeV2;
  if (!splitNode) {
    return undefined;
  }

  const mergeNode = graphStore.getMergeNode(splitNode);
  if (!mergeNode) {
    return undefined;
  }

  const existingEdgeIds = new Set(graphStore.edges.keys());

  const getNodeLabel = (edgeId: string): string | undefined =>
    graphStore.getNodeToIncomingEdge(edgeId)?.data.label;

  const suggestedBranches: SplitBranchWithName[] = (
    suggestionMeta.branches ?? []
  ).map((branch, index) => ({
    ...branch,
    name: resolveBranchName(
      branch,
      index,
      graphStore.getNodeToIncomingEdge(branch.edge_id)?.data.label,
    ),
  }));
  const suggestedBranchEdgeIds = new Set(
    suggestedBranches
      .map((branch) => branch?.edge_id)
      .filter((id): id is string => !!id),
  );

  // Identify branches to delete and add
  // delete branches that are not in the suggestion
  const branchesToDelete = splitNode.data.branches.filter(
    (branch) => branch?.edge_id && !suggestedBranchEdgeIds.has(branch.edge_id),
  );
  // add branches that are in the suggestion but not in the graphs edges
  const branchesToAdd = suggestedBranches.filter(
    (branch) => branch?.edge_id && !existingEdgeIds.has(branch.edge_id),
  );

  // Find existing branches that have name changes in the suggestion
  // (compare suggested name with current node label)
  const branchNameUpdates = suggestedBranches.filter((branch) => {
    if (!branch?.edge_id || !existingEdgeIds.has(branch.edge_id)) {
      return false;
    }
    const existingLabel = getNodeLabel(branch.edge_id);
    return existingLabel !== undefined && existingLabel !== branch.name;
  });

  // Early return if nothing to do
  if (
    branchesToDelete.length === 0 &&
    branchesToAdd.length === 0 &&
    branchNameUpdates.length === 0
  ) {
    return undefined;
  }

  const currentEdges = graphStore.edgesArray;

  // Handle deletions first
  const { deletedNodeIds, edgesToDelete } =
    handleBranchDeletionsForSplitNodeSuggestion(
      graphStore,
      branchesToDelete,
      splitNode,
      currentEdges,
    );

  // Handle additions
  const { nodesToCreate, edgesToCreate } =
    handleBranchAdditionsForSplitNodeSuggestion(
      branchesToAdd,
      splitNode,
      mergeNode,
    );

  // Handle name updates for existing branches
  const updatedBranchNodes: Record<string, BeMappedNode> = {};
  for (const branch of branchNameUpdates) {
    const edge = graphStore.edges.get(branch.edge_id);
    if (edge) {
      const branchNode = graphStore.nodes.get(edge.target);
      if (branchNode) {
        updatedBranchNodes[branchNode.id] = {
          ...branchNode,
          data: {
            ...branchNode.data,
            label: branch.name,
          },
        };
      }
    }
  }

  // Update split node with final branches from suggestion
  const updatedSplitNode: SplitNodeV2 = {
    ...splitNode,
    data: {
      ...splitNode.data,
      branches: suggestedBranches,
      default_edge_id:
        suggestionMeta.default_edge_id ?? splitNode.data.default_edge_id,
    },
  };

  // Merge nodes: deletions as null, additions as nodes, name updates, updated split node
  const nodesUpdate: Record<string, BeMappedNode | null> = {
    ...Object.fromEntries(Array.from(deletedNodeIds).map((id) => [id, null])),
    ...nodesToCreate,
    ...updatedBranchNodes,
    [updatedSplitNode.id]: updatedSplitNode,
  };

  // Merge edges: deletions as null, additions as edges
  const edgesUpdate: Record<string, Edge | null> = {
    ...Object.fromEntries(
      Array.from(edgesToDelete).map((edge) => [edge.id, null]),
    ),
    ...edgesToCreate,
  };

  // Handle group updates
  const newBranchNodeIds = Object.keys(nodesToCreate).filter(
    (id) => id !== splitNode.id,
  );
  const groupsUpdate = getGroupUpdatesForSplitNodeSuggestion(
    graphStore,
    deletedNodeIds,
    newBranchNodeIds,
    splitNode,
  );

  // Determine operation type based on what changed
  let operation: Operation;
  if (branchesToDelete.length > 0) {
    operation = Operation.DELETE_BRANCH;
  } else if (branchesToAdd.length > 0) {
    operation = Operation.CREATE_BRANCH;
  } else {
    operation = Operation.EDIT_NODE;
  }

  // If deleting branches, also return branch info for the modal
  const branchToDeleteInfo = getBranchDeletionInfo(
    graphStore,
    branchesToDelete,
    splitNode,
    currentEdges,
  );

  return {
    nodesUpdate,
    edgesUpdate,
    groupsUpdate,
    operation,
    ...(branchToDeleteInfo && { branchToDeleteInfo }),
  };
};
